import React, { useState } from 'react';
import { useThoughtSpotAuth } from '../hooks/useThoughtSpotAuth';
import LiveboardEmbed from './LiveboardEmbed';
import BEM from 'common/bem';

const classes = BEM.with('ClinicalWorkflows');

// ThoughtSpot liveboard IDs for each embedded view
const LIVEBOARD_IDS: Record<string, string> = {
  'nurse-call': '1d5df012-2905-410d-b283-228fcdbbdda3',
  consults: '3b94194a-b437-41c0-a642-7020b4c6db41',
};

type InsightsView = 'my-liveboards' | 'nurse-call' | 'consults';

const INSIGHTS_VIEWS: { id: InsightsView; label: string }[] = [
  { id: 'my-liveboards', label: 'My Liveboards' },
  { id: 'nurse-call', label: 'Nurse Call' },
  { id: 'consults', label: 'Consults' },
];

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: Record<string, any> | null;
  currentOrganizationId: string;
};

export default function InsightsTab({ client, currentOrganizationId }: Props) {
  const [activeInsightsView, setActiveInsightsView] = useState<InsightsView>('my-liveboards');
  const authState = useThoughtSpotAuth(client, currentOrganizationId);

  const activeLiveboardId = LIVEBOARD_IDS[activeInsightsView];

  const renderContent = () => {
    // My Liveboards has no liveboard ID — placeholder until future implementation
    if (!activeLiveboardId) {
      return (
        <div className={classes('insights-placeholder')}>
          <div className={classes('insights-placeholder-icon')}>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M3 3v18h18" />
              <path d="m19 9-5 5-4-4-3 3" />
            </svg>
          </div>
          <h3 className={classes('insights-placeholder-title')}>Coming Soon</h3>
          <p className={classes('insights-placeholder-text')}>
            My Liveboards insights will be available here.
          </p>
        </div>
      );
    }

    if (authState.status === 'loading' || authState.status === 'idle') {
      return <div className={classes('insights-loading')}>Loading analytics…</div>;
    }

    if (authState.status === 'error') {
      return (
        <div className={classes('insights-error')}>
          Unable to load analytics. Please try again later.
        </div>
      );
    }

    return (
      <LiveboardEmbed
        liveboardId={activeLiveboardId}
        host={authState.host}
        token={authState.token}
        orgId={authState.orgId}
      />
    );
  };

  return (
    <div className={classes('insights-split')} data-test-id="clinical-workflows-insights-content">
      <div className={classes('insights-nav')}>
        {INSIGHTS_VIEWS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={classes('insights-nav-item', { selected: activeInsightsView === id })}
            onClick={() => setActiveInsightsView(id)}
            data-test-id={`clinical-workflows-insights-${id}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className={classes('insights-content')}>{renderContent()}</div>
    </div>
  );
}
